import React, { ReactNode, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Language } from '../language/Language';
import styles from './styles.module.scss';
import { Link } from 'react-router-dom';
import hamburger from './assets/hamburger.svg';
import close from './assets/close.svg';
import classNames from 'classnames';
import { Donate } from '@/features/changeDonate/ui/Donate';
import { useIsMobile } from '@/shared/utils';
import { UserButtons } from './ui/UserButtons/UserButtons';
import { AppTooltip } from '@igo/ui';
import { Feedback } from '@/features/changeFeedback/ui/Feedback';
import { ThemeToggle } from '../theme-toggle/ThemeToggle';
import { MainLogo } from '@shared/ui/MainLogo/MainLogo';
import { MenuSport } from './ui/MenuSport/MenuSport';
import { MenuGame } from './ui/MenuGame/MenuGame';
import { MenuPublications } from './ui/MenuPublications/MenuPublications';
import ArrowIcon from './assets/ArrowUp.svg?react';
import { NotificationsCenter } from '@/features/notifications/ui/NotificationsCenter';

const HOVER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const TOOLTIP_HIDE_DELAY = 200;

const useCanHover = () => {
    const [canHover, setCanHover] = useState(
        () => typeof window.matchMedia !== 'function' || window.matchMedia(HOVER_MEDIA_QUERY).matches
    );

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return;

        const mediaQuery = window.matchMedia(HOVER_MEDIA_QUERY);
        const handleChange = (event: MediaQueryListEvent) => setCanHover(event.matches);

        setCanHover(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleChange);

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    return canHover;
};

export const Header: React.FC = () => {
    const [active, setActive] = useState(false);
    const isMobile = useIsMobile(900);
    const canHover = useCanHover();
    const [tooltipContent, setTooltipContent] = useState<ReactNode | null>(null);
    const [menuOpen, setMenuOpen] = useState<'game' | 'sport' | 'publications' | undefined>();

    const isListNewsEnable = process.env.ENABLE_LIST_NEWS === 'true';
    const isNotificationCenterEnable = process.env.ENABLE_NOTIFICATION_CENTER === 'true';

    const handleToggle = () => {
        setActive((prev) => {
            document.body.style.overflow = prev ? '' : 'hidden';
            return !prev;
        });
        setMenuOpen(undefined);
    };
    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <div style={{ display: 'flex' }}>
                    <span className={styles.burger} onClick={handleToggle}>
                        <img src={hamburger} alt="BurgerMenu" />
                    </span>
                    <MainLogo />
                </div>

                <nav className={classNames(styles.navbar, { [styles['navbar_active']]: active })}>
                    <span className={styles.close} onClick={handleToggle}>
                        <img src={close} alt="close menu" />
                    </span>
                    <span
                        className={classNames(styles['navbar-item'], { [styles.mobileItem]: isMobile })}
                        onMouseEnter={!isMobile && canHover ? () => setTooltipContent(<MenuSport />) : undefined}
                        onFocus={!isMobile ? () => setTooltipContent(<MenuSport />) : undefined}
                        onClick={
                            isMobile
                                ? () => setMenuOpen((prev) => (prev === 'sport' ? undefined : 'sport'))
                                : !canHover
                                  ? () => setTooltipContent(<MenuSport />)
                                  : undefined
                        }
                        tabIndex={!isMobile ? 0 : undefined}
                        aria-haspopup={!isMobile ? 'menu' : undefined}
                        data-tooltip-id={isMobile ? undefined : 'header-end-tooltip'}
                        data-tooltip-place="bottom"
                    >
                        <Trans i18nKey="menuSport" />
                        {isMobile && <ArrowIcon className={menuOpen === 'sport' ? styles.arrowOpen : undefined} />}
                    </span>
                    {isMobile && (
                        <div className={classNames(styles.subMenu, { [styles.subMenu__close]: menuOpen !== 'sport' })}>
                            <MenuSport active={active} handleToggle={handleToggle} />
                        </div>
                    )}
                    <span
                        className={classNames(styles['navbar-item'], { [styles.mobileItem]: isMobile })}
                        onMouseEnter={!isMobile && canHover ? () => setTooltipContent(<MenuGame />) : undefined}
                        onFocus={!isMobile ? () => setTooltipContent(<MenuGame />) : undefined}
                        onClick={
                            isMobile
                                ? () => setMenuOpen((prev) => (prev === 'game' ? undefined : 'game'))
                                : !canHover
                                  ? () => setTooltipContent(<MenuGame />)
                                  : undefined
                        }
                        tabIndex={!isMobile ? 0 : undefined}
                        aria-haspopup={!isMobile ? 'menu' : undefined}
                        data-tooltip-id={isMobile ? undefined : 'header-end-tooltip'}
                        data-tooltip-place="bottom"
                    >
                        <Trans i18nKey="menuGame" />
                        {isMobile && <ArrowIcon className={menuOpen === 'game' ? styles.arrowOpen : undefined} />}
                    </span>
                    {isMobile && (
                        <div className={classNames(styles.subMenu, { [styles.subMenu__close]: menuOpen !== 'game' })}>
                            <MenuGame active={active} handleToggle={handleToggle} />
                        </div>
                    )}

                    {isListNewsEnable && (
                        <span
                            className={classNames(styles['navbar-item'], { [styles.mobileItem]: isMobile })}
                            onMouseEnter={
                                !isMobile && canHover ? () => setTooltipContent(<MenuPublications />) : undefined
                            }
                            onFocus={!isMobile ? () => setTooltipContent(<MenuPublications />) : undefined}
                            onClick={
                                isMobile
                                    ? () =>
                                          setMenuOpen((prev) => (prev === 'publications' ? undefined : 'publications'))
                                    : !canHover
                                      ? () => setTooltipContent(<MenuPublications />)
                                      : undefined
                            }
                            tabIndex={!isMobile ? 0 : undefined}
                            aria-haspopup={!isMobile ? 'menu' : undefined}
                            data-tooltip-id={isMobile ? undefined : 'header-end-tooltip'}
                            data-tooltip-place="bottom"
                        >
                            <Trans i18nKey="menuPublications" />
                            {isMobile && (
                                <ArrowIcon className={menuOpen === 'publications' ? styles.arrowOpen : undefined} />
                            )}
                        </span>
                    )}
                    {isListNewsEnable && isMobile && (
                        <div
                            className={classNames(styles.subMenu, {
                                [styles.subMenu__close]: menuOpen !== 'publications',
                            })}
                        >
                            <MenuPublications active={active} handleToggle={handleToggle} />
                        </div>
                    )}

                    <Link className={styles['navbar-item']} to="/about" onClick={active ? handleToggle : undefined}>
                        <Trans i18nKey="menuAboutCompany" />
                    </Link>

                    {isMobile && <Donate onClick={active ? handleToggle : undefined} />}
                    {isMobile && <Feedback onClick={active ? handleToggle : undefined} />}
                </nav>
                <div className={styles.end}>
                    {!isMobile && <Donate />}
                    {!isMobile && <Feedback />}
                    <div className={styles.notificationArea}>
                        {isNotificationCenterEnable && <NotificationsCenter />}
                        <ThemeToggle setTooltipContent={setTooltipContent} />
                        <Language setTooltipContent={setTooltipContent} />
                        <UserButtons setTooltipContent={setTooltipContent} />
                    </div>
                    <AppTooltip
                        id="header-end-tooltip"
                        className={styles.notPadding}
                        clickable
                        noArrow
                        openOnClick={isMobile || !canHover}
                        closeEvents={isMobile ? { click: true } : undefined}
                        delayHide={TOOLTIP_HIDE_DELAY}
                        globalCloseEvents={{ escape: true, clickOutsideAnchor: isMobile || !canHover }}
                    >
                        <div>{tooltipContent}</div>
                    </AppTooltip>
                </div>
            </header>
        </div>
    );
};
